// @ts-strict-ignore
import { FileDescriptor, FileState, FileStorageType } from 'rainbow-web-sdk/models/common/fileDescriptor';
import { BubbleRB } from 'rainbow-web-sdk/models/bubble.model';
import { FileViewer } from 'rainbow-web-sdk/models/common/fileViewer';
import { BehaviorSubject, Subject, Subscription, filter } from "rxjs";
import { FilePublicDownload } from 'rainbow-web-sdk/models/common/filePublicDownload';
import { distinctUntilChanged } from "rxjs/internal/operators/distinctUntilChanged";

import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { BubbleConferenceRecap, BubbleConferenceRecapFile, BubbleConferenceRecapRB } from 'rainbow-web-sdk/models/bubbleConferenceRecap.model';
import { Service } from 'rainbow-web-sdk/services/service';
import { ConversationRB } from 'rainbow-web-sdk/models/conversation.model';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { ProfileService } from "rainbow-web-sdk/services/core/profile.service";
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import xmlParser, { XMLElement } from 'rainbow-web-sdk/utils/xmlParser';
import { MessageID } from '../../models';

export const FILE_STORAGE_SVC = 'FileStorageService';

export interface GetFilesResultInterface {
    fileDescriptors: FileDescriptor[]; // the list of the file descriptors returned by the API
    allReceived: boolean; // if there're more files that we can load from the server
}

/**
 * The user's quota and the consumption of its storage space.
 */
export interface ConsumptionData {
    /**
     * The current consumption (in bytes). If there is no limit, this data is set to undefined.
     */
    currentValue: number;

    /**
     * The quota (in bytes) associated with the offer available to the user.
     */
    maxValue: number;

    /**
     * The offer available to the user.
     */
    feature: string;

    /**
     * The unit of measurement for consumption and quota information.
     */
    unit: string;

    /**
     * Details about the current consumption (in bytes) of its storage space.
     */
    details: {
        /**
         * Consumption of files belonging to the user, whether these files are shared or not.
         */
        others: number;

        /**
         * Consumption of recording files belonging to the user, whether these files are shared or not.
         */
        recordings: number;
    };

}

export interface TranscriptionData {
    fileId: string;
    transcriptionMessage: string;
    action: string;
    error: string;
}

export class FileStorageExternalStorage {
    /**
     * The external fileserver endpoint
     */
    endPoint: string;
    /**
     * Set to true when the temporary token must be renewed, or not exists.
     */
    authenticationNeeded: boolean;
    /**
     * Human readable string to identify the external storage
     */
    name: string;
    /**
     * Human readable string to identify the external storage
     */
    description: string;
    /**
     * Human readable string to identify the external storage
     */
    type: FileStorageType = FileStorageType.RAINBOW;
}

export enum FileStorageServiceEvents {

    /**
     * @event RAINBOW_FILESTORAGE_NEED_POPUP
     * @description
     * This RB Event is send when the fileStorageService needs to open a window popup and that user has blocked the popup usage.
     * This event is gnerally used to display an information message.
     */
    RAINBOW_FILESTORAGE_NEEDS_POPUP = 'RAINBOW_FILESTORAGE_NEED_POPUP',

    ON_FILE_REMOVED_FROM_STORE_EVENT = "ON_FILE_REMOVED_FROM_STORE_EVENT",
    ON_FILE_DESCRIPTOR_RECEIVED_EVENT = "ON_FILE_DESCRIPTOR_RECEIVED_EVENT",
    ON_CONSUMPTION_DATA_UPDATED_EVENT = "ON_CONSUMPTION_DATA_UPDATED_EVENT",
    ON_CONSUMPTION_DATA_WARNING_EVENT = "ON_CONSUMPTION_DATA_WARNING_EVENT",
    ON_TRANSCRIPTION_CREATED = "ON_TRANSCRIPTION_CREATED",
    ON_TRANSCRIPTION_SUBMITTED = "ON_TRANSCRIPTION_SUBMITTED",
    ON_TRANSCRIPTION_ABORTED = "ON_TRANSCRIPTION_ABORTED",
    ON_ARCHIVE_AVAILABLE = "ON_ARCHIVE_AVAILABLE"

}

export enum FileStorageCapabilities {
    THUMBNAILS = 'THUMBNAILS',
    SAVE = 'SAVE',
    TRANSFERT = 'TRANSFERT'
}

export class FileStorageService extends Service {

    protected started: boolean = false;
    protected listeners: any[] = [];
    protected portalURL: string;
    protected consumptionData: ConsumptionData;
    protected currentOrder: string = 'date';
    protected voiceMessageFileDescriptors: FileDescriptor[] = [];
    protected fileDescriptors: FileDescriptor[] = [];
    protected receivedFileDescriptors: FileDescriptor[] = [];
    protected mergedFileDescriptors: FileDescriptor[] = [];
    protected uploadingFileDescriptors: FileDescriptor[] = [];
    protected externalFileDescriptors: FileDescriptor[] = [];

    protected contactService: ContactService = ContactService.getInstance();
    protected logger: LoggerService = LoggerService.getInstance();
    protected errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private mainService: MainService = MainService.getInstance();
    private profileService = ProfileService.getInstance();
    private xmppManagementHandler: any = null;
    public deleteFileWoConfirm: boolean = false;
    public fileDescriptorsSubject: Subject<any> = null;
    public uploadingFileDescriptorsSubject: Subject<any> = null;
    protected conferenceRecordingFiles: BubbleConferenceRecap[] = [];
    private conferenceRecordingFileDescriptors: FileDescriptor[] = [];
    public conferenceRecordingFilesSubject: Subject<BubbleConferenceRecap[]> = null;
    public sharedWithMeConferenceRecordingFiles: FileDescriptor[] = [];
    public sharedWithMeConferenceRecordingFilesSubject: Subject<FileDescriptor[]> = null;
    public allSentFilesLoaded: boolean = false;
    public allReceivedFilesLoaded: boolean = false;
    //protected transcriptionData: TranscriptionData;

    // External Storage
    private externalStorageFileDescriptors?: FileDescriptor[];
    private externalStoragePath: string = '';
    public externalStorage?: FileStorageExternalStorage;
    private xmppService: XmppService = XmppService.getInstance();

    protected publicFileDownload: FilePublicDownload[] = [];
    public publicFileDownloadSubject: Subject<any> = null;
    public startedSubject = new BehaviorSubject<boolean>(false);

    private retrieveFileDescriptorPromises: any[] = [];
    private authService: AuthService = AuthService.getInstance();

    // the FD already got for a given conversation (p2p or room)
    //contains the folowing information {init: boolean, offset: number, total: number, allReceived: boolean}
    private fileDescriptorsForConversationResult = {};

    //mark all requests
    private searchForFileDescriptorsCache = {};

    private allFilesLoaded: boolean = false;

    private rxSubject: Subject<RBEvent<FileStorageServiceEvents>> = new Subject<RBEvent<FileStorageServiceEvents>>();

    /* **************************************************** */
    /* * LIFECYCLE STUFF                                  * */
    /* **************************************************** */
    public static getInstance() {
        return Service.create(FILE_STORAGE_SVC, FileStorageService);
    }

    public static build() {
        return new FileStorageService();
    }

    protected constructor() {
        super();
    }

    public async start(fileStorageServiceConfig: any = null) {
        this.started = false;
        this.portalURL = config.restServerUrl + "/api/rainbow/filestorage/v1.0/files";
        this.fileDescriptors = [];
        this.receivedFileDescriptors = [];
        this.mergedFileDescriptors = [];
        this.voiceMessageFileDescriptors = [];
        this.fileDescriptorsSubject = new Subject<any>();
        this.uploadingFileDescriptorsSubject = new Subject<any>();
        this.publicFileDownload = [];
        this.publicFileDownloadSubject = new Subject<any>();
        this.conferenceRecordingFilesSubject = new Subject<any>();
        this.sharedWithMeConferenceRecordingFilesSubject = new Subject<any>();

        // Listen to changes in option value to start the service
        this.contactService.userContact.fileStorageEnabledSubject
            .pipe(distinctUntilChanged())
            .subscribe(async (fileStorageEnabled: boolean) => {
                if (!fileStorageEnabled) { this.logger.info(`start skipped - fileStorageEnabled : false`); return; }

                this.logger.info("[FileStorageService] === STARTING ===");

                try {
                    this.consumptionData = null;
                    await this.retrieveFilesAtStartUp();

                    this.started = true;

                    this.logger.info("[FileStorageService] === STARTED ===");
                    this.startedSubject.next(true);

                    // Listen to changes in option value to start the service
                    this.contactService.userContact.fileStorageEnabledSubject
                        .pipe(distinctUntilChanged())
                        .subscribe((fileStorageEnable) => {
                            if (!fileStorageEnable) {
                                this.stop();
                                this.startedSubject.next(false);
                            }
                        });
                }
                catch (error) {
                    this.logger.error("[FileStorageService] === STARTING === failure -- " + error.message);
                    this.startedSubject.next(false);
                }

                if (!fileStorageServiceConfig?.webinarGuest) {
                    this.retrieveUserConsumption();
                    this.retrieveVoiceMessageFileDescriptorsListPerOwner()
                        .then(() => {
                            this.logger.info("[FileStorageService] === STARTED (retrieveVoiceMessageFileDescriptorsListPerOwner)");
                        })
                        .catch(() => {
                            this.logger.error("[FileStorageService] === STARTING === failure -- (retrieveVoiceMessageFileDescriptorsListPerOwner)");
                        });
                }
            });
        this.attachHandlers();
    }

    public async stop() {
        this.logger.info("[FileStorageService] === STOPPING ===");
        if (this.started) { this.started = false; }
        if (this.listeners) { this.listeners.forEach((unregisterListener) => { unregisterListener(); }); }
        this.voiceMessageFileDescriptors = [];
        this.fileDescriptors = [];
        this.receivedFileDescriptors = [];
        this.uploadingFileDescriptors = [];
        this.conferenceRecordingFiles = [];
        this.sharedWithMeConferenceRecordingFiles = [];
        this.conferenceRecordingFileDescriptors = [];
        this.fileDescriptorsForConversationResult = {};
        this.allFilesLoaded = false;
        this.removeHandlers();
        this.logger.info("[FileStorageService] === STOPPED ===");
    }

    public async reinit() {
        if (this.contactService?.userContact && !this.contactService.userContact.fileStorageEnabled) {
            this.logger.info(`reinit skipped - fileStorageEnabled : ${this.contactService.userContact.fileStorageEnabled}`);
            return;
        }
        this.retrieveFilesAtStartUp(100);
        this.attachHandlers();
    }

    private attachHandlers() {
        this.removeHandlers();
        this.xmppManagementHandler = this.xmppService.addHandler((stanza) => { this.onManagementMessageReceived(stanza); return true; }, null, "message", "management");
    }

    private removeHandlers() {
        this.logger.debug(`[FileStorageService] removeHandlers `);
        // Remove all handlers
        if (this.xmppManagementHandler) {
            this.xmppService.deleteHandler(this.xmppManagementHandler);
            this.xmppManagementHandler = null;
        }
    }

    /**
     * @public
     * @function subscribe
     * @instance
     * @description Subscribe to updates from the fileStorageService (all events are of RBEvent type {name: string, data:any});
     * @param {any} handler The call-back function that will be subscribed to the RxJS subject
     * @param {string[]} eventNames (optional) array of event to listen
     * @returns {Subscription} Returns RxJS Subscription
     */
    public subscribe(handler: any, eventNames?: string | string[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;

        return observable.subscribe((event: RBEvent) => {
            try { handler(event); }
            catch (error) { this.logger.error(`[FileStorageService] subscribe () -- error -- ${error.message}`); }
        });
    }

    public sendEvent(name: FileStorageServiceEvents, data?: any): void {
        this.rxSubject.next(RBEvent.create<FileStorageServiceEvents, any>(name, data));
    }

    //API USED BY VOICE MESSAGES SERVICE
    //perhaps to update when we get some new voice messages as they're seen as FD;
    public async getLatestFilesFromServer() {
        await this.retrieveFilesAtStartUp(50);
    }

    private async retrieveFilesAtStartUp(limit: number = 1000, offset: number = 0) {
        try {
            this.logger.info(`[FileStorageService] retrieveFilesAtStartUp -- ${limit} -- ${offset}`);
            const url = `${this.portalURL}/viewers/${this.contactService.userContact.dbId}?format=full&limit=${limit}&offset=${offset}&overall=true&sortField=uploadedDate&sortOrder=-1`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const fileDescriptorsResponse = await response.json();
            const fileDescriptorsData = fileDescriptorsResponse.data;

            // Check if we have received all fileDescriptors
            const total = fileDescriptorsResponse.total;

            const fileDescriptors = fileDescriptorsData.reduce((fds: FileDescriptor[], fdData: any) => {
                if (fdData?.tags?.purpose !== 'webinar_record') fds.push(this.createFileDescriptorFromData(fdData));
                return fds;
            }, []);

            this.logger.info(`[FileStorageService] retrieveFilesAtStartUp success (total files available on server ${total})`);

            fileDescriptors.forEach((fd) => {
                if (fd.ownerId === this.contactService.userContact.dbId) {
                    if (this.fileDescriptors.findIndex(fd2 => (fd2.id === fd.id)) === -1) { this.fileDescriptors.push(fd); }
                }
                else {
                    if (this.receivedFileDescriptors.findIndex(fd2 => (fd2.id === fd.id)) === -1) { this.receivedFileDescriptors.push(fd); }
                }
            });

            if (limit + offset > total) {
                this.logger.info(`[FileStorageService] retrieveFilesAtStartUp success -- all files loaded -- ${total}`);
                this.allFilesLoaded = true;
            }
            else if (total < 5000 && !this.allFilesLoaded) {
                //load all files, if they're less than 5k
                this.retrieveFilesAtStartUp(1000, offset + limit);
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveFilesAtStartUp');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public removeRoomFileViewer(room: BubbleRB): void {
        if (this.contactService?.userContact && !this.contactService.userContact.fileStorageEnabled) {
            this.logger.info(`removeRoomFileViewer skipped - fileStorageEnabled : ${this.contactService.userContact.fileStorageEnabled}`);
            return;
        }

        this.logger.info("[FileStorageService] >removeRoomFileViewer: " + room.dbId);

        //remove all files shared with me, but not those that I've shared with the bubble
        this.receivedFileDescriptors.forEach((file) => {
            if (file && file.ownerId !== this.contactService.userContact.dbId) {
                const viewerFound = file.viewers.find((element) => { return element.viewerId === room.dbId; });
                if (viewerFound) {
                    this.logger.info("[FileStorageService] >viewer found delete it");
                    file.viewers.splice(file.viewers.indexOf(viewerFound), 1);
                }
            }
        });

        for (let index = 0; index < this.receivedFileDescriptors.length; index++) {
            const fileDescriptor = this.receivedFileDescriptors[index];
            if (fileDescriptor && (!fileDescriptor.viewers?.length)) {
                this.logger.info("[FileStorageService] >receivedFileDescriptor empty, delete it");
                this.receivedFileDescriptors.splice(index, 1);
                index -= 1;
            }
        }
    }

    /**
     * Method request for the list of files received by a user from a given peer (i.e. inside a given conversation)
     * @param {string} peerId [optional] dbId of contact in the P2P conversation
     * @param {string} roomId [required] dbId of the room
     * @returns {Promise<FileDescriptor[]>} : list of received files descriptors
     * @memberof FileStorageService
     */
    private async retrieveFilesInConversation(peerId: string = "", roomId: string = "", offset: number = 0, limit: number = 1000): Promise<FileDescriptor[]> {
        try {
            let url = "";
            if (peerId) {
                url = `${this.portalURL}/viewers/${this.contactService.userContact.dbId}?ownerId=${peerId}&format=full&limit=${limit}&offset=${offset}&sortField=uploadedDate&sortOrder=-1&overall=true`;
            }
            else if (roomId) {
                url = `${this.portalURL}/viewers/${roomId}?format=full&limit=${limit}&offset=${offset}&sortField=uploadedDate&sortOrder=-1&overall=true`;
            }

            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const fileDescriptorsResponse = await response.json();
            const fileDescriptorsData = fileDescriptorsResponse.data;
            const total = fileDescriptorsResponse.total;

            //store the current state for the given conversation
            if (!this.fileDescriptorsForConversationResult[peerId ? peerId : roomId].offset) { this.fileDescriptorsForConversationResult[peerId ? peerId : roomId].offset = limit; }
            else { this.fileDescriptorsForConversationResult[peerId ? peerId : roomId].offset += limit; }

            this.fileDescriptorsForConversationResult[peerId ? peerId : roomId].total = total;

            if (this.fileDescriptorsForConversationResult[peerId ? peerId : roomId].offset >= this.fileDescriptorsForConversationResult[peerId ? peerId : roomId].total) {
                this.fileDescriptorsForConversationResult[peerId ? peerId : roomId].allReceived = true;
            }

            const fileDescriptors = fileDescriptorsData.map((fileDescriptorData: any) => {
                return this.createFileDescriptorFromData(fileDescriptorData);
            });
            this.logger.info("[FileStorageService] retrieveFilesInConversation success");

            fileDescriptors.forEach((fd) => {
                if (fd.ownerId === this.contactService.userContact.dbId) {
                    if (this.fileDescriptors.findIndex(fd2 => (fd2.id === fd.id)) === -1) { this.fileDescriptors.push(fd); }
                }
                else {
                    if (this.receivedFileDescriptors.findIndex(fd2 => (fd2.id === fd.id)) === -1) { this.receivedFileDescriptors.push(fd); }
                }
            });

            return this.fileDescriptors;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveFilesInConversation');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method used to search for files from the cached files based on different criteria
     * @param {string} fileSet [optional] fileSet, could be "all", "mine", "received"; By default it's all
     * @param {string} fileName [optional]  to search by filename
     * @param {string} typeMIME [optional]  to filter by typeMIME (values allowd: image, audio-video, pdf, presentation, document, spreadsheet)
     * @param {string} sortField [optional]  sortField, could be fileName, uploadedDate or size; By default it's uploadedDate
     * @param {number} sortOrder [optional]  sortOrder, could be 1 (normal order) or -1 (reverse order); By default, it's -1
     * @returns {Promise<GetFilesResultInterface>} Object containing the { fileDescriptors: FileDescriptor[], limit: number, offset: number }
     * @memberof FileStorageService
     */
    private async searchForFilesInCache(fileSet: string = "all", fileName: string = "", typeMIME: string = "", sortField: string = "uploadedDate", sortOrder: number = -1, url: string = ""): Promise<GetFilesResultInterface> {
        let fileDescriptors: FileDescriptor[];
        if (fileSet === "all") { fileDescriptors = this.fileDescriptors.concat(this.receivedFileDescriptors); }
        else if (fileSet === "mine") { fileDescriptors = this.fileDescriptors; }
        else if (fileSet === "received") { fileDescriptors = this.receivedFileDescriptors; }

        //filter files based on criterias
        if (typeMIME) {
            switch (typeMIME) {
                case 'image':
                    fileDescriptors = fileDescriptors.filter((fileDescriptor: FileDescriptor) => {
                        return fileDescriptor.typeMIME?.startsWith('image');
                    });
                    break;
                case 'audio-video':
                    fileDescriptors = fileDescriptors.filter((fileDescriptor: FileDescriptor) => {
                        return fileDescriptor.typeMIME && (fileDescriptor.typeMIME.startsWith('video') || fileDescriptor.typeMIME.startsWith('audio'));
                    });
                    break;
                case 'rec':
                    fileDescriptors = fileDescriptors.filter((fileDescriptor: FileDescriptor) => {
                        return fileDescriptor.typeMIME && fileDescriptor.typeMIME === 'audio/webm';
                    });
                    break;
                default:
                    fileDescriptors = fileDescriptors.filter((fileDescriptor: FileDescriptor) => {
                        return fileDescriptor.typeMIME && this.getMimeInfo(fileDescriptor.typeMIME).commonType === typeMIME;
                    });
                    break;
            }
        }

        if (fileName) {
            const searchCriteria = fileName.toLowerCase().trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            fileDescriptors = fileDescriptors.filter((fileDescriptor: FileDescriptor) => {
                return ((fileDescriptor.fileName.toLowerCase().trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "")).indexOf(searchCriteria) !== -1);
            });
        }

        switch (sortField) {
            case 'name':
            case 'fileName':
                fileDescriptors.sort((fd1: FileDescriptor, fd2: FileDescriptor) => {
                    if (sortOrder === 1) return fd1.fileName.localeCompare(fd2.fileName);

                    return fd2.fileName.localeCompare(fd1.fileName);
                });
                break;
            case 'size':
                fileDescriptors.sort((fd1: FileDescriptor, fd2: FileDescriptor) => {
                    if (sortOrder === 1) return fd1.size - fd2.size;
                    return fd2.size - fd1.size;
                });
                break;
            default:
                fileDescriptors.sort((fd1: FileDescriptor, fd2: FileDescriptor) => {
                    if (sortOrder === 1) return fd1.time - fd2.time;
                    return fd2.time - fd1.time;
                });
                break;
        }

        return { fileDescriptors, allReceived: (this.allFilesLoaded || this.searchForFileDescriptorsCache[url].allReceived) };
    }

    /* ******************************************************** */

    /* *                    PUBLIC APIs                         *

    *  *  Basic accessors to FileStorageService's properties  * */
    /* ******************************************************** */

    public getFileDescriptorById(id: string): FileDescriptor {
        for (const fileDescriptor of this.fileDescriptors) { if (fileDescriptor.id === id) { return fileDescriptor; } }
        for (const fileDescriptor of this.receivedFileDescriptors) { if (fileDescriptor.id === id) { return fileDescriptor; } }
        for (const fileDescriptor of this.externalFileDescriptors) { if (fileDescriptor.id === id) { return fileDescriptor; } }
        for (const fileDescriptor of this.sharedWithMeConferenceRecordingFiles) { if (fileDescriptor.id === id) { return fileDescriptor; } }
        for (const fileDescriptor of this.conferenceRecordingFileDescriptors) { if (fileDescriptor.id === id) { return fileDescriptor; } }
        return null;
    }

    public removeFileDescriptorById(id: string): any {
        const index = this.fileDescriptors.findIndex(fileDescriptor => fileDescriptor.id === id);
        if (index !== -1) this.fileDescriptors.splice(index, 1);
        const receivedInd = this.receivedFileDescriptors.findIndex(fileDescriptor => fileDescriptor.id === id);
        if (receivedInd !== -1) this.receivedFileDescriptors.splice(receivedInd, 1);
    }

    /**
     * Method retrieve the data usage of a given user
     * @returns {Promise<ConsumptionData>} : object data with the following properties:
     *                  - feature {String} : The feature key belonging to the user's profile
     *                  - maxValue {number} : The quota associated to this offer [octet]
     *                  - currentValue {number} : The user's current consumption [octet]
     *                  - unit {String} : The unit of this counters
     */
    public async retrieveUserConsumption(): Promise<ConsumptionData> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/filestorage/v1.0/users/consumption`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const firstTime = this.consumptionData?.currentValue;
            this.consumptionData = (await response.json()).data as ConsumptionData;
            this.logger.info(`[FileStorageService] retrieveUserConsumption success - ${JSON.stringify(this.consumptionData)}`);
            this.eventService.publish('ON_CONSUMPTION_DATA_UPDATED_EVENT');
            this.sendEvent(FileStorageServiceEvents.ON_CONSUMPTION_DATA_UPDATED_EVENT, this.consumptionData);

            if (firstTime && this.consumptionData?.currentValue && this.consumptionData?.maxValue) {
                if (this.consumptionData?.currentValue / this.consumptionData.maxValue >= 0.9) {
                    //send warning event
                    this.eventService.publish('ON_CONSUMPTION_DATA_WARNING_EVENT');
                    this.sendEvent(FileStorageServiceEvents.ON_CONSUMPTION_DATA_WARNING_EVENT, this.consumptionData);
                }
            }
            return this.consumptionData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveUserConsumption');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method request to get the files shared in a given bubble.
     * @param bubbleId -Id of the room
     * @returns  list of received files descriptors
     */
    public async retrieveReceivedFilesForNewRoom(bubbleId: string): Promise<void> {
        try {
            if (!this.fileDescriptorsForConversationResult[bubbleId]) {
                this.fileDescriptorsForConversationResult[bubbleId] = { init: true };
            }
            await this.retrieveFilesInConversation("", bubbleId);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveReceivedFilesForRoom');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method to load more files inside a given conversation (shared or received)
     * @param {ConversationRB} conversation the conversation where we want to get the latest 1000 files
     * @returns {Promise<GetFilesResultInterface>} : object {fileDescriptors:FileDescriptor[], allReceived:boolean }
     * list of files descriptors; And allReceived flag to indicate if we can "load" more files for the same conversation
     * @memberof FileStorageService
     */
    public async loadMoreDescriptorsForConversation(conversation: ConversationRB): Promise<GetFilesResultInterface> {
        if (!conversation) throw new Error("[FileStorageService] loadMoreDescriptorsForConversation -- missing conversation");

        const dbId = conversation.contact ? conversation.contact.dbId : conversation.bubble.dbId;
        if (!this.fileDescriptorsForConversationResult[dbId]) {
            //this is the first request, return the initial API;
            return this.getFileDescriptorsInConversation(conversation);
        }

        if (this.allFilesLoaded || this.fileDescriptorsForConversationResult[dbId].allReceived) {
            //all files are already loaded, return the results directly
            return this.getFileDescriptorsInConversation(conversation);
        }

        await this.retrieveFilesInConversation(conversation.contact ? conversation.contact.dbId : "", conversation.bubble ? conversation.bubble.dbId : "", this.fileDescriptorsForConversationResult[dbId].offset, 30);

        return this.getFileDescriptorsInConversation(conversation);
    }

    /**
     * Method get the list of files inside a given conversation (shared or received) Limited to the first 1000 files;
     * If you need more files, you should use the loadMoreDescriptorsForConversation API;
     * @param {ConversationRB} conversation the conversation where we want to get the latest 1000 files
     * @returns {Promise<GetFilesResultInterface>} : object {fileDescriptors:FileDescriptor[], allReceived:boolean }
     * list of received files descriptors, limited to 1000 files; And allReceived flag to indicate if we can "load" more files for the same conversation
     * @memberof FileStorageService
     */
    public async getFileDescriptorsInConversation(conversation: ConversationRB): Promise<GetFilesResultInterface> {
        if (!conversation) throw new Error("[FileStorageService] getFileDescriptorsInConversation -- missing conversation");

        const dbId = conversation.contact ? conversation.contact.dbId : conversation.bubble.dbId;
        let receivedFiles = [];
        if (conversation.contact) {
            if (!this.allFilesLoaded && !this.fileDescriptorsForConversationResult[dbId]) {
                this.fileDescriptorsForConversationResult[dbId] = { init: true };
                await this.retrieveFilesInConversation(dbId);
            }

            receivedFiles = this.receivedFileDescriptors.filter((file) => {
                if (conversation.contact.dbId === file.ownerId) {
                    return file.viewers.find((viewer: FileViewer) => { return viewer.viewerId === this.contactService.userContact.dbId; }) !== undefined;
                }
                return false;
            });
        }
        if (conversation.bubble) {
            if (!this.allFilesLoaded && !this.fileDescriptorsForConversationResult[dbId]) {
                this.fileDescriptorsForConversationResult[dbId] = {
                    init: true
                };
                await this.retrieveFilesInConversation("", dbId);
            }

            receivedFiles = this.receivedFileDescriptors.filter((file) => {
                if (file.ownerId !== this.contactService.userContact.dbId) {
                    return file.viewers.find((viewer: FileViewer) => { return viewer.viewerId === conversation.bubble.dbId; }) !== undefined;
                }
                return false;
            });
        }

        const sentFiles = this.fileDescriptors.filter((file) => {
            for (let i = 0; i < file.viewers.length; i++) {
                if (file.viewers[i].viewerId === dbId) { return true; }
            }
            return false;
        });

        return { fileDescriptors: sentFiles.concat(receivedFiles), allReceived: this.allFilesLoaded || this.fileDescriptorsForConversationResult[dbId].allReceived };
    }

    /**
     * Method used to search for files on the server based on different criteria
     * @param {string} fileSet [optional] fileSet, could be "all", "mine", "received"; By default it's all
     * @param {string} fileName [optional]  to search by filename
     * @param {string} typeMIME [optional]  to filter by typeMIME (values allowd: image, audio-video, pdf, presentation, document, spreadsheet)
     * @param {string} sortField [optional]  sortField, could be fileName, uploadedDate or size; By default it's uploadedDate
     * @param {number} sortOrder [optional]  sortOrder, could be 1 (normal order) or -1 (reverse order); By default, it's -1
     * @param {number} offset offset, in case of paginated result
     * @returns {Promise<GetFilesResultInterface>} Object containing the { fileDescriptors: FileDescriptor[], limit: number, offset: number }
     * @memberof FileStorageService
     */
    public async searchForFilesOnServer(fileSet: string = "all", fileName: string = "", typeMIME: string = "", sortField: string = "uploadedDate", sortOrder: number = -1, offset: number = 0, force: boolean = false, onlyInternal: boolean = false): Promise<GetFilesResultInterface> {
        try {
            // Build URL
            let url = `${this.portalURL}/viewers/${this.contactService.userContact.dbId}`;
            if (fileSet === "mine") url = this.portalURL;
            url += "?format=full&limit=1000";

            if (fileSet === "all") url += "&overall=true";
            if (sortField) url += `&sortField=${sortField}`;
            if (sortOrder) url += `&sortOrder=${sortOrder}`;
            if (typeMIME) url += `&shortcutMIME=${typeMIME}`;
            if (fileName) url += `&fileName=${fileName}`;
            if (offset) url += `&offset=${offset}`;
            if (onlyInternal) url += `&backendType=Swift`;

            // Check if all files are already loaded
            // Or request already done and get files from cache (no cache when searching for filename though)
            if (!force && (this.allFilesLoaded || (!fileName && this.searchForFileDescriptorsCache[url]))) {
                return this.searchForFilesInCache(fileSet, fileName, typeMIME, sortField, sortOrder, url);
            }

            //otherwise use server API to get the files
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const fileDescriptorsResponse = await response.json();
            const fileDescriptorsData = fileDescriptorsResponse.data;
            const total = fileDescriptorsResponse.total;

            this.searchForFileDescriptorsCache[url] = {
                allReceived: (total < offset + 1000)
            };

            const fileDescriptors = fileDescriptorsData.reduce((fds: FileDescriptor[], fdData: any) => {
                if (fdData?.tags?.purpose !== 'webinar_record') {
                    fds.push(this.createFileDescriptorFromData(fdData));
                }
                return fds;
            }, []);

            // Add files in the fileDescriptors cache
            fileDescriptors.forEach((fd) => {
                if (fd.ownerId === this.contactService.userContact.dbId) {
                    if (this.fileDescriptors.findIndex(fd2 => (fd2.id === fd.id)) === -1) { this.fileDescriptors.push(fd); }
                }
                else {
                    if (this.receivedFileDescriptors.findIndex(fd2 => (fd2.id === fd.id)) === -1) { this.receivedFileDescriptors.push(fd); }
                }
            });

            this.logger.info("[FileStorageService] searchForFilesOnServer success");
            return { fileDescriptors, allReceived: this.searchForFileDescriptorsCache[url].allReceived };
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'searchForFilesOnServer');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method used to search for files on the server based on different criteria
     * @param {string} fileSet [optional] fileSet, could be "all", "mine", "received"; By default it's all
     * @returns {GetFilesResultInterface} Object containing the { fileDescriptors: FileDescriptor[], allReceived: boolean }
     * @memberof FileStorageService
     */
    /*public getAllStoredFiles(fileSet: string = "all"): GetFilesResultInterface {
        if (fileSet === "mine") return { fileDescriptors: this.fileDescriptors, allReceived: this.allFilesLoaded };
        if (fileSet === "received") return { fileDescriptors: this.receivedFileDescriptors, allReceived: this.allFilesLoaded };

        return { fileDescriptors: this.fileDescriptors.concat(this.receivedFileDescriptors), allReceived: this.allFilesLoaded };
    }*/

    public getConsumptionData(): ConsumptionData { return this.consumptionData; }

    public getVoiceMessageFileDescriptorByMsgId(msgId: string): FileDescriptor {
        return this.voiceMessageFileDescriptors.find((file) => {
            return (file.tags !== null && file.tags.msgId === msgId);
        });
    }

    /* ******************************************************** */
    /* *  FILE DESCRIPTOR CREATION/DELETION APIs              * */
    /* ******************************************************** */

    /**
     * Method requests server to create a file descriptor this will be saved to local file descriptor list (i.e. this.fileDescriptors)
     * @param {File} file [required] file for which file descriptor has to be created
     * @param {FileViewer[]} viewers [required] list of viewers having access to the file (a viewer could be either be a user or a room)
     * @returns {Promise<FileDescriptor>} file descriptor created by server or error
     * @memberof FileStorageService
     */
    public async createFileDescriptor(file: File, viewers: FileViewer[] = [], voiceMessageData = null, ccarelogs: boolean = false, ccareclientlogs: boolean = false): Promise<FileDescriptor> {
        try {
            return await this.createFileDescriptorInternal(file, viewers, voiceMessageData, ccarelogs, ccareclientlogs);
        }
        catch (error) {
            if (this.externalStorage && error?.errorDetailsCode === 401230) {
                this.logger.warn(`[FileStorageService] createFileDescriptor -- externalStorage -- ${error?.error?.errorDetails}`);
                await this.authenticateExternalStorage();
                return await this.createFileDescriptorInternal(file, viewers, voiceMessageData, ccarelogs, ccareclientlogs);
            }
            throw error;
        }
    }

    private async createFileDescriptorInternal(file: File, viewers: FileViewer[] = [], voiceMessageData = null, ccarelogs: boolean = false, ccareclientlogs: boolean = false): Promise<FileDescriptor> {
        const fileName = file.name;
        const extension = file.name.split(".").pop();
        const size = file.size;
        try {
            const data: any = { fileName, extension, size, viewers };

            if (voiceMessageData) {
                data.voicemessage = true;
                data.encoding = true;
                data.duration = voiceMessageData.duration;

                if (data.duration === 0) {
                    data.duration = 1;
                }
            }
            if (ccarelogs) {
                data.ccarelogs = true;
                data.ccareclientlogs = ccareclientlogs;
            }

            const url = this.portalURL;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify(data);
            const response: Response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) { throw (response); }
            const fileDescriptorData = (await response.json()).data;
            const fileDescriptor = this.createFileDescriptorFromData(fileDescriptorData);
            if (fileDescriptor) {
                this.fileDescriptors.push(fileDescriptor);
                this.uploadingFileDescriptors.push(fileDescriptor);

                this.uploadingFileDescriptorsSubject.next({ size: this.getUploadingFilesSize() });
            }
            this.logger.info("[FileStorageService] createFileDescriptor -- " + fileDescriptor.id + " -- success");
            return fileDescriptor;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'createFileDescriptor');
            if (errorInfo.error.errorDetailsCode === 403153) {
                this.contactService.userContact.fileSharingEnabled = false;
            }
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    private createFileDescriptorFromData(data: any): FileDescriptor {
        let viewers: FileViewer[] = [];
        if (data.viewers) { viewers = FileViewer.createFromData(data.viewers); }
        let url = data.url;
        if (!url) { url = config.restServerUrl + "/api/rainbow/fileserver/v1.0/files/" + data.id; }

        const state = data.isUploaded ? FileState.UPLOADED : FileState.NOT_UPLOADED;
        const voiceMessage = (data.tags && data.tags.purpose === "voicemail");
        const recordingFile = (data.tags && data.tags.purpose === "conference_record");
        const recordingConferenceId = recordingFile && data.tags && data.tags.conferenceRecording ? data.tags.conferenceRecording.filesConfRecordingId : null;

        let virusScanState = "unknown";
        if (data.isClean === null) { virusScanState = "notScanned"; }
        else if (data.isClean === true) { virusScanState = "isClean"; }
        else { virusScanState = "isInfected"; }

        const fd = FileDescriptor.create(data.id, url, data.ownerId, data.fileName, data.extension, data.typeMIME,
            data.size, data.registrationDate, data.uploadedDate, data.dateToSort, viewers, state, data.thumbnail, data.orientation, data.original_w, data.original_h, data.tags, virusScanState, voiceMessage, recordingFile, recordingConferenceId, data.transcriptionStatus, data.transcription);
        fd.externalStorage = data.backendType !== undefined && data.backendType !== 'Swift';
        return fd;
    }

    /**
     * Method to delete a given File Descriptor from the server. Only usable for the FD owned by the user;
     * @param {string} id [required] fthe File Descriptor id to be deleted from the server
     * @returns {Promise<void>} Resovle once done
     * @memberof FileStorageService
     */
    public async deleteFileDescriptor(id: string) {
        try {
            const url = `${this.portalURL}/${id}`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) { throw (response); }
            this.logger.info("[FileStorageService] deleteFileDescriptor -- SUCCESS");
            this.removeUploadingFileFromId(id);
            this.deleteFileDescriptorFromCache(id);
            this.eventService.publish("ON_FILE_REMOVED_FROM_STORE_EVENT", { fileId: id });
            this.sendEvent(FileStorageServiceEvents.ON_FILE_REMOVED_FROM_STORE_EVENT, { fileId: id });
            this.retrieveUserConsumption();
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deleteFileDescriptor');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    private deleteAllFilesDescriptorsFromCache() {
        this.logger.info("[FileStorageService] deleteAllFilesDesceiptorsFromCache");

        this.fileDescriptors.forEach((fileDescriptor) => {
            fileDescriptor.setState(FileState.DELETED);
            this.receivedFileDescriptors.forEach((receivedFileDescriptor, index) => {
                if (receivedFileDescriptor.id === fileDescriptor.id) {
                    this.receivedFileDescriptors.splice(index, 1);
                }
            });
        });
        this.fileDescriptors = [];
        this.uploadingFileDescriptors = [];

        this.fileDescriptorsSubject.next({ action: 'listUpdate' });
    }

    /**
     * Method to delete all File Descriptor that belong to the user from the server.
     * @returns {Promise<void>} Resolve once done
     * @memberof FileStorageService
     */
    public async deleteAllFileDescriptor() {
        try {
            const url = `${this.portalURL}/all`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) { throw (response); }
            this.logger.info("[FileStorageService] deleteAllFileDescriptor -- success");
            this.deleteAllFilesDescriptorsFromCache();
            this.consumptionData = (await response.json()).data as ConsumptionData;
            this.eventService.publish('ON_CONSUMPTION_DATA_UPDATED_EVENT');
            this.sendEvent(FileStorageServiceEvents.ON_CONSUMPTION_DATA_UPDATED_EVENT, this.consumptionData);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deleteAllFileDescriptor');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method retrieve full list of files belonging to user making the request
     * @returns {Promise<FileDescriptor[]>} return
     * @memberof FileStorageService
     */
    public async retrieveVoiceMessageFileDescriptorsListPerOwner(): Promise<FileDescriptor[]> {
        try {
            const url = `${this.portalURL}?format=full&path=/voice-messages&sortField=uploadedDate&sortOrder=-1`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const fileDescriptorsData = (await response.json()).data;
            if (!fileDescriptorsData) return [];

            // Create file descriptors
            this.voiceMessageFileDescriptors = fileDescriptorsData.reduce((fileDescriptors: FileDescriptor[], fileDescriptorData: any) => {
                const fileDescriptor = this.createFileDescriptorFromData(fileDescriptorData);
                if (fileDescriptor.state === FileState.NOT_UPLOADED) {
                    this.logger.info(`[FileStorageService] file ${fileDescriptor.fileName} not uploaded`);
                    if (fileDescriptor && fileDescriptor.ownerId === this.contactService.userContact.dbId) {
                        this.logger.info(`[FileStorageService] file ${fileDescriptor.fileName} is our property, we can delete it`);
                        this.deleteFileDescriptor(fileDescriptor.id);
                    }
                    else this.logger.info(`[FileStorageService] file ${fileDescriptor.fileName} is NOT our property, we CANNOT delete it`);
                }
                else fileDescriptors.push(fileDescriptor);
                return fileDescriptors;
            }, []);

            this.logger.info("[FileStorageService] retrieveVoiceMessageFileDescriptorsListPerOwner -- success");
            return this.voiceMessageFileDescriptors;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveVoiceMessageFileDescriptorsListPerOwner');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method deletes a viewer from the list of viewer of a given file
     * @param {string} viewerId [required] Identifier of viewer to be removed. Could be either a user or a room
     * @param {string} fileId [required] Identifier of the fileDescriptor from which the viewer will be removed
     * @returns {Promise<FileDescriptor>} return
     * @memberof FileStorageService
     */
    public async deleteFileViewer(viewerId: string, fileId: string): Promise<FileDescriptor> {
        try {
            const url = `${this.portalURL}/${fileId}/viewers/${viewerId}`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) { throw (response); }
            this.logger.info(`[FileStorageService] deleteFileViewer ${response.statusText}`);
            const fileDescriptor = this.getFileDescriptorById(fileId);
            if (fileDescriptor) {
                const index = fileDescriptor.viewers.findIndex((viewer: FileViewer) => { return viewer.viewerId === viewerId; });
                if (index !== -1) { fileDescriptor.viewers.splice(index, 1); }
                fileDescriptor.stateSubject.next(fileDescriptor.state);
            }
            return fileDescriptor;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deleteFileViewer');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method adds a viewer to a given file on server if it is not already one
     * @param {string} fileId [required] Identifier of file
     * @param {string} viewerId [required] Identifier of viewer to be added
     * @param {string} type [required] type of viewer to be added (user or room)
     * @returns {Promise<FileDescriptor>} file descriptor with newly added viewer
     * @memberof FileStorageService
     */
    public async addFileViewer(fileId: string, viewerId: string, type: string): Promise<FileDescriptor> {
        let fileDescriptor = this.getFileDescriptorById(fileId);
        if (!fileDescriptor) {
            fileDescriptor = await this.retrieveOneFileDescriptor(fileId);
        }

        if (fileDescriptor) {
            if (fileDescriptor.isAlreadyFileViewer(viewerId)) { return fileDescriptor; }

            try {
                const url = `${this.portalURL}/${fileId}/viewers`;
                const headers = this.authService.getPostHeader();
                const body = JSON.stringify({ viewerId, type });
                const response: Response = await fetch(url, { method: 'POST', headers, body });
                if (!response.ok) { throw (response); }

                this.logger.info("[FileStorageService] addFileViewer success");
                const viewerAdded: FileViewer = FileViewer.createFromData([{ viewerId, type }])[0];
                fileDescriptor.viewers.push(viewerAdded);
                fileDescriptor.stateSubject.next(fileDescriptor.state);
                return fileDescriptor;
            }
            catch (error) {
                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'addFileViewer');
                this.logger.error(`[fileStorageService] ${errorInfo.message}`);
                throw errorInfo.error;
            }
        }
        return null;
    }

    /**
     * This method is used to copy the file to user personal storage
     * @param {string} fileId [required] Identifier of file descriptor to copy
     * @returns {Promise<FileDescriptor>} file descriptor retrieved
     * @memberof FileStorageService
     */
    public async copyFileToUserCloudStorage(fileId: string): Promise<FileDescriptor> {
        try {
            const url = `${this.portalURL}/${fileId}/copy`;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'POST', headers });
            if (!response.ok) { throw (response); }
            const responseData = (await response.json()).data;
            this.logger.info(`[FileStorageService] copyFileToUserCloudStorage | copyFile ${fileId} -- success`);
            const fileDesc = await this.retrieveAndStoreOneFileDescriptor(responseData.id, true);
            if (fileDesc.ownerId === this.contactService.userContact.dbId) { this.retrieveUserConsumption(); }
            return fileDesc;
        }
        catch (error) {

            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'copyFileToUserCloudStorage');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);

            throw errorInfo.error;
        }
    }

    /**
     * Method retrieve a specific file descriptor from server
     * @param {string} fileId [required] Identifier of file descriptor to retrieve
     * @returns {Promise<FileDescriptor>} file descriptor retrieved
     * @memberof FileStorageService
     */
    public retrieveOneFileDescriptor(fileId: string): Promise<FileDescriptor> {
        if (this.retrieveFileDescriptorPromises[fileId]) {
            this.logger.info(`[FileStorageService] retrieveOneFileDescriptor for ${fileId} already lauched`);
            return this.retrieveFileDescriptorPromises[fileId];
        }

        this.retrieveFileDescriptorPromises[fileId] = (async () => {
            try {
                this.logger.info(`[FileStorageService] retrieveOneFileDescriptor ${fileId}`);
                const url = `${this.portalURL}/${fileId}?format=full`;
                const headers = this.authService.getRequestHeader();
                const response: Response = await fetch(url, { method: 'GET', headers });
                if (!response.ok) { throw (response); }
                const responseData = (await response.json()).data;
                const fileDescriptor = this.createFileDescriptorFromData(responseData);
                this.logger.info(`[FileStorageService] retrieveOneFileDescriptor ${fileId} -- success`);
                delete this.retrieveFileDescriptorPromises[fileId];
                return fileDescriptor;
            }
            catch (error) {
                const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveOneFileDescriptor');
                this.logger.debug(`[fileStorageService] ${errorInfo.message}`);
                delete this.retrieveFileDescriptorPromises[fileId];
                throw errorInfo.error;
            }
        })();
        return this.retrieveFileDescriptorPromises[fileId];
    }

    /**
     * Method retrieve a specific file descriptor from server and stores it in local fileDescriptors (replace existing and add if new)
     * @param {string} fileId [required] Identifier of file descriptor to retrieve
     * @returns {Promise<FileDescriptor>} file descriptor retrieved or null if none found
     * @memberof FileStorageService
     */
    public retrieveAndStoreOneFileDescriptor(fileId: string, forceRetrieve: boolean = false): Promise<FileDescriptor> {

        let fileDescriptor = this.getFileDescriptorById(fileId);
        if (fileDescriptor && !forceRetrieve) {
            this.logger.info("[FileStorageService] retrieveAndStoreOneFileDescriptor -- return existing fileDescriptor " + fileId);
            return Promise.resolve(fileDescriptor);
        }

        return this.retrieveOneFileDescriptor(fileId)
            .then((retrievedFileDescriptor: any) => {

                if (retrievedFileDescriptor.recordingFile) {
                    if (retrievedFileDescriptor.ownerId !== this.contactService.userContact.dbId) {
                        this.sharedWithMeConferenceRecordingFiles.unshift(retrievedFileDescriptor);
                        this.sharedWithMeConferenceRecordingFilesSubject.next(this.sharedWithMeConferenceRecordingFiles);
                    }
                    else {
                        // A recording file descriptor that is ours
                        const currentFileDescriptor = this.conferenceRecordingFileDescriptors.find((_fileDescriptor) => { return _fileDescriptor.id === retrievedFileDescriptor.id; });

                        if (currentFileDescriptor) { currentFileDescriptor.update(retrievedFileDescriptor); }
                        else { this.conferenceRecordingFileDescriptors.push(retrievedFileDescriptor); }
                    }
                    return Promise.resolve(retrievedFileDescriptor);
                }

                //update the current file descriptor, if here
                fileDescriptor = this.getFileDescriptorById(fileId);

                if (fileDescriptor?.isImage()) {
                    retrievedFileDescriptor.previewBlob = fileDescriptor.previewBlob;
                    retrievedFileDescriptor.previewBlobSubject.next(retrievedFileDescriptor.previewBlob);
                }
                // Update old file descriptor in this.fildeDescriptors
                const currentFileDescriptor = this.fileDescriptors.find((_fileDescriptor) => { return _fileDescriptor.id === retrievedFileDescriptor.id; });

                if (currentFileDescriptor) {
                    currentFileDescriptor.update(retrievedFileDescriptor);
                }

                // Update old file descriptor in this.receivedFileDescriptors
                const currentReceivedFileDescriptor = this.receivedFileDescriptors.find((_fileDescriptor) => { return _fileDescriptor.id === retrievedFileDescriptor.id; });
                if (currentReceivedFileDescriptor) {
                    currentReceivedFileDescriptor.update(retrievedFileDescriptor);
                }

                if (!currentFileDescriptor && !currentReceivedFileDescriptor) {
                    if (retrievedFileDescriptor.ownerId === this.contactService.userContact.dbId) { // The file is mine
                        this.fileDescriptors.push(retrievedFileDescriptor);
                        this.logger.info("[FileStorageService] retrieveAndStoreOneFileDescriptor -- fileDescriptor " + retrievedFileDescriptor.id + " -- now stored in my files");
                    } else { // The file is not mine
                        this.receivedFileDescriptors.push(retrievedFileDescriptor);
                        this.logger.info("[FileStorageService] retrieveAndStoreOneFileDescriptor -- fileDescriptor " + retrievedFileDescriptor.id + " -- now stored in received files");
                    }
                    this.fileDescriptorsSubject.next({ action: 'new', fd: retrievedFileDescriptor });
                }

                this.eventService.publish("ON_FILE_DESCRIPTOR_RECEIVED_EVENT", { fileId: retrievedFileDescriptor.id });
                this.sendEvent(FileStorageServiceEvents.ON_FILE_DESCRIPTOR_RECEIVED_EVENT, { fileId: retrievedFileDescriptor.id });

                return Promise.resolve(retrievedFileDescriptor);
            })
            .catch((error) => {
                if (error.status >= 400 && error.status < 500) {
                    if (fileDescriptor) {
                        // ATTENTION MODIFIED BY MCO
                        //if (error.status === 404) { fileDescriptor.state = FileState.DELETED; }
                        //else { this.deleteFileDescriptorFromCache(fileDescriptor.id, true); }
                        if (error.status === 404) { this.deleteFileDescriptorFromCache(fileDescriptor.id); }

                        this.logger.debug("[FileStorageService] Sending ON_FILE_REMOVED_FROM_STORE_EVENT");
                        this.eventService.publish("ON_FILE_REMOVED_FROM_STORE_EVENT", { fileId: fileDescriptor.id });
                        this.sendEvent(FileStorageServiceEvents.ON_FILE_REMOVED_FROM_STORE_EVENT, { fileId: fileDescriptor.id });

                    }
                }

                return Promise.reject(error);
            });
    }

    /**********************************************************/
    /**  Utilities                                           */
    /**********************************************************/
    public deleteFileDescriptorFromCache(id: string) {
        this.logger.info("[FileStorageService] deleteFileDescriptorFromCache " + id);

        for (let index = 0; index < this.receivedFileDescriptors.length; index++) {
            const fileDescriptor = this.receivedFileDescriptors[index];
            if (fileDescriptor.id === id) {
                this.receivedFileDescriptors.splice(index, 1);
                fileDescriptor.setState(FileState.DELETED);
                break;
            }
        }

        for (let index = 0; index < this.sharedWithMeConferenceRecordingFiles.length; index++) {
            const fileDescriptor = this.sharedWithMeConferenceRecordingFiles[index];
            if (fileDescriptor.id === id) {
                this.sharedWithMeConferenceRecordingFiles.splice(index, 1);
                this.sharedWithMeConferenceRecordingFilesSubject.next(this.sharedWithMeConferenceRecordingFiles);
                fileDescriptor.setState(FileState.DELETED);
                break;
            }
        }

        for (let index = 0; index < this.fileDescriptors.length; index++) {
            const fileDescriptor = this.fileDescriptors[index];
            if (fileDescriptor.id === id) {
                this.fileDescriptors.splice(index, 1);
                fileDescriptor.setState(FileState.DELETED);
                break;
            }
        }
    }

    public extractFileIdFromUrl(url: string): string {
        const parts = url.split("/");
        const fileDescriptorId = parts.pop() || parts.pop();
        return fileDescriptorId;
    }

    public retrieveAndStoreOneVoiceMessageFileDescriptor(fileId: string) {
        this.retrieveOneFileDescriptor(fileId)
            .then((retrievedFileDescriptor: FileDescriptor) => {
                if (retrievedFileDescriptor.tags && retrievedFileDescriptor.tags.path === "/voice-messages") {
                    const fileDescriptorExist = this.voiceMessageFileDescriptors.find((file) => {
                        return file.id === fileId;
                    });
                    if (fileDescriptorExist !== undefined) {
                        // Remove old file descriptor with same id if there's one
                        const fileDescriptorIndex = this.voiceMessageFileDescriptors.findIndex((oneFileDescriptor) => { return oneFileDescriptor.id === retrievedFileDescriptor.id; });
                        if (fileDescriptorIndex > -1) {
                            this.voiceMessageFileDescriptors.splice(fileDescriptorIndex, 1, retrievedFileDescriptor);
                        }
                        this.logger.info("[FileStorageService] getRetrieveAndStoreOneVoiceMessageFileDescriptor " + fileId + " -- success");
                    }
                    else {
                        this.voiceMessageFileDescriptors.push(retrievedFileDescriptor);
                    }
                }
            });
    }

    public deleteVoiceMessageFileDescriptor(fileId) {
        this.logger.info("[FileStorageService] deleteVoiceMessageFileDescriptore " + fileId);

        const fileDescriptorExist = this.voiceMessageFileDescriptors.find((file) => {
            return file.id === fileId;
        });

        if (fileDescriptorExist !== undefined) {
            const fileDescriptorIndex = this.voiceMessageFileDescriptors.findIndex((oneFileDescriptor) => { return oneFileDescriptor.id === fileDescriptorExist.id; });
            if (fileDescriptorIndex > -1) {
                this.voiceMessageFileDescriptors.splice(fileDescriptorIndex, 1);
            }
        }
    }

    public updateAFileDescriptorLocally(fileDesc: FileDescriptor): void {
        this.fileDescriptors = this.fileDescriptors.map((fd) => {
            if (fd.id === fileDesc.id) {
                return fileDesc;
            }
            return fd;
        });
        this.receivedFileDescriptors = this.receivedFileDescriptors.map((fd) => {
            if (fd.id === fileDesc.id) {
                return fileDesc;
            }
            return fd;
        });
    }

    public removeRecordFromRecordingFile(deletedFileId: string, recordingFileId: string) {
        this.logger.info("[FileStorageService] removeRecordFromRecordingFile " + deletedFileId + " belonging to " + recordingFileId);

        const matchingRecordingFile = this.conferenceRecordingFiles.find((recordingFile: BubbleConferenceRecap) => {
            return recordingFile.id === recordingFileId;
        });

        if (matchingRecordingFile) {
            const indexTosuppr = matchingRecordingFile.records.findIndex((confRecord: BubbleConferenceRecapFile) => {
                return confRecord.fileId === deletedFileId;
            });

            matchingRecordingFile.records.splice(indexTosuppr, 1);

            // If no records left, delete the file
            if (matchingRecordingFile.records.length === 0) {
                this.logger.info(`[FileStorageService] removeRecordFromRecordingFile - no associated record left, remove ${recordingFileId} from conferenceRecordingFiles`);
                const filteredConferenceRecordingFiles: BubbleConferenceRecap[] = this.conferenceRecordingFiles.filter((recordingFile: BubbleConferenceRecap) => {
                    return recordingFile.id !== recordingFileId;
                });
                this.conferenceRecordingFiles = filteredConferenceRecordingFiles;
                this.conferenceRecordingFilesSubject.next(this.conferenceRecordingFiles);
            }
        }

    }

    /////////////////////////////////////////////////////
    ///           MANAGE PUBLIC DOWNLOADS             ///

    public getPublicFileDownloads() {
        return this.publicFileDownload;
    }

    public clearPublicFileDownloads() {
        this.publicFileDownload = [];
    }

    public findPublicFileDownload(fileUrl: string): FilePublicDownload {
        return this.publicFileDownload.find((element: FilePublicDownload) => {
            return (element.url === fileUrl);
        });
    }

    public updatePublicFileDownloadArray(filesPublicDownload: [], fileSystem: any) {

        this.logger.info("[FileStorageService] >updatePublicFileDownloadArray");

        filesPublicDownload.forEach((element) => {
            const fileDownload = this.findPublicFileDownload(element["url"]);
            if (fileDownload) {
                this.logger.info("[FileStorageService] file (" + element["url"] + ") Found - to update");
                fileDownload.receivedBytes = element["receivedBytes"];
                fileDownload.savePath = element["savePath"];

                if (fileDownload.isDownloadFinished() && fileDownload.openFolderOnceDownloadFinished) {
                    this.logger.debug("[FileStorageService] showItemInFolder auto on download completion");
                    fileSystem.showItemInFolder(fileDownload.savePath);
                }
                this.logger.debug("[FileStorageService] fileName=" + fileDownload.fileName);
                this.logger.debug("[FileStorageService] file receivedBytes=" + fileDownload.receivedBytes);
                this.logger.debug("[FileStorageService] file totalBytes=" + fileDownload.totalBytes);
                this.logger.debug("[FileStorageService] file savePath=" + fileDownload.savePath);
                fileDownload.progressSubject.next(undefined);
            } else {
                this.logger.info("[FileStorageService] file Not Found");
                const newFile = FilePublicDownload.create(element["url"], element["fileName"], element["totalBytes"], element["receivedBytes"], element["savePath"]);
                this.publicFileDownload.unshift(newFile);
            }
            this.publicFileDownloadSubject.next(undefined);
        });
    }

    public getUploadingFilesSize() {
        let filesSize = 0;
        this.uploadingFileDescriptors.forEach((fileDescriptor) => {
            filesSize += fileDescriptor.size;
        });
        return filesSize;
    }

    public removeUploadingFileFromId(fileId: string) {
        const file = this.uploadingFileDescriptors.find((fileDesc: any) => {
            return fileDesc.id === fileId;
        });
        if (file) this.removeUploadingFileDescriptor(file);
    }

    public removeUploadingFileDescriptor(fileDesc: FileDescriptor) {
        if (fileDesc.state === FileState.UPLOADED) {
            const consumption = this.getConsumptionData();
            if (consumption) {
                const usedSpace = (consumption["currentValue"] !== 0) ? consumption["currentValue"] : 0;
                consumption["currentValue"] = usedSpace + fileDesc.size;
            }
        }
        const index = this.uploadingFileDescriptors.indexOf(fileDesc);
        if (index > -1) {
            this.uploadingFileDescriptors.splice(index, 1);
            this.uploadingFileDescriptorsSubject.next({ size: this.getUploadingFilesSize() });
        }
    }

    public isEnoughAvailableSpaceForFiles(filesToSend: any[]): boolean {

        if (!filesToSend.length) return true;

        let newFilesSize = 0;
        //if file.fd already exist, so we already have the file in the storage, so ignore it's size;
        filesToSend.forEach((attachmentFile) => {
            if (!attachmentFile.fd) {
                if (attachmentFile.file) {
                    newFilesSize += attachmentFile.file.size;
                } else if (attachmentFile.size) {
                    newFilesSize += attachmentFile.size;
                }
            }
        });

        const consumption = this.getConsumptionData();
        const totalAvailableSpace = consumption["maxValue"];
        const usedSpace = (consumption["currentValue"] !== 0) ? consumption["currentValue"] : 0;
        const remainingSpace = totalAvailableSpace - usedSpace;
        const filesUploadingSize = this.getUploadingFilesSize();
        this.logger.debug(`[FileStorageService] isEnoughAvailableSpaceForFiles - totalAvailableSpace: ${totalAvailableSpace} - usedSpace: ${usedSpace} - filesUploadingSize: ${filesUploadingSize}`);

        return newFilesSize < (remainingSpace - filesUploadingSize);
    }

    public isEnoughAvailableSpaceForFileDescriptor(fileId: string, fileViewers: any): boolean {
        if (!fileId) {
            return true;
        }

        const fileDescriptor = this.getFileDescriptorById(fileId);

        const consumption = this.getConsumptionData();
        const totalAvailableSpace = consumption["maxValue"];
        const usedSpace = (consumption["currentValue"] !== 0) ? consumption["currentValue"] : 0;
        const remainingSpace = totalAvailableSpace - usedSpace;
        const filesUploadingSize = this.getUploadingFilesSize();

        return fileDescriptor.size * fileViewers.length < (remainingSpace - filesUploadingSize);
    }

    public stopAllFileTransfer(): void {
        this.fileDescriptors.forEach((fileDesc) => {
            if (fileDesc) {
                if (fileDesc.abortTransferController) fileDesc.abortTransferController.abort();
                if (fileDesc.abortXMLHttpRequestArray?.length) {
                    fileDesc.abortXMLHttpRequestArray.map((func) => { func(); });
                }
            }
        });
    }

    public getMimeInfo(mimeType: string): any {
        let icon: string;
        let img: boolean = false;
        let commonType: string;

        const splitMime = mimeType.split("/");
        switch (splitMime[0]) {
            case "text":
                switch (splitMime[1]) {
                    case "csv": icon = "txt-type"; commonType = "csv"; break;
                    case "css":
                    case "html":
                        icon = "code-type"; commonType = "code";
                        break;
                    default: icon = "txt-type"; commonType = "txt"; break;
                }
                break;
            case "image":
                switch (splitMime[1]) {
                    case "svg+xml": icon = "image-type"; commonType = "image"; break;
                    case "vnd.adobe.photoshop":
                    case "photoshop":
                    case "x-photoshop":
                    case "psd":
                        icon = "psd-type"; commonType = "psd";
                        break;
                    default: icon = "image-type"; img = true; commonType = "image"; break;
                }
                break;
            case "audio": icon = "audio-type"; commonType = "audio"; break;
            case "video": icon = "video-type"; commonType = "video"; break;
            case "application":
                switch (splitMime[1]) {
                    case "pdf": icon = "pdf-type"; commonType = "pdf"; break;
                    case "msword":
                    case "vnd.openxmlformats-officedocument.wordprocessingml.document":
                        icon = "document-type"; commonType = "document";
                        break;
                    case "vnd.ms-excel":
                    case "vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                    case "vnd.openxmlformats-officedocument.spreadsheetml.template":
                    case "vnd.ms-excel.sheet.macroEnabled.12":
                        icon = "excel-type"; commonType = "spreadsheet";
                        break;
                    case "vnd.ms-powerpoint":
                    case "vnd.openxmlformats-officedocument.presentationml.presentation":
                    case "vnd.openxmlformats-officedocument.presentationml.slideshow":
                        icon = "powerpoint-type"; commonType = "presentation";
                        break;
                    case "illustrator":
                    case "postscript":
                        icon = "ai-type"; commonType = "ai";
                        break;
                    case "photoshop":
                    case "psd":
                        icon = "psd-type"; commonType = "psd";
                        break;
                    case "javascript":
                    case "json":
                    case "typescript":
                    case "xml":
                        icon = "code-type"; commonType = "code";
                        break;
                    case "zip":
                    case "x-rar-compressed":
                    case "x-7z-compressed":
                    case "x-tar":
                        icon = "archive-type"; commonType = "archive";
                        break;
                    default: icon = "unknown-type"; commonType = "unknown"; break;
                }
                break;
            default: icon = "unknown-type"; commonType = "unknown"; break;
        }
        return { icon: `/assets/images/sprite.svg#${icon}`, img, commonType };
    }

    /* RECORDING FILES MANAGEMENT */

    public removePublicFileDownload(file: FilePublicDownload) {
        if (!file?.url) return;
        const index = this.publicFileDownload.indexOf(file);
        if (index >= 0) {
            this.publicFileDownload.splice(index, 1);
            this.publicFileDownloadSubject.next(undefined);
        }
    }

    public async getRecordingFileAvatar(recordingFileId: string): Promise<string> {
        try {
            const url = config.restServerUrl + "/api/rainbow/filestorage/v1.0/conferences-recordings/avatar/" + recordingFileId + "?thumbnail=true";
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const blob = await response.blob();
            const src = URL.createObjectURL(blob);
            return src;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getRecordingFileAvatar');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public getRecordingFiles() {
        this.conferenceRecordingFilesSubject.next(this.conferenceRecordingFiles);
        return this.conferenceRecordingFiles;
    }

    public async getRecordingFilesFromServer(): Promise<BubbleConferenceRecap[]> {
        try {
            // const url = config.restServerUrl + `/api/rainbow/filestorage/v1.0/conferences-recordings?format=full&limit=1000&sortOrder=-1&viewerId=${this.contactService.userContact.dbId}&overall=true`;
            const url = config.restServerUrl + `/api/rainbow/filestorage/v1.0/conferences-recordings?format=full&limit=1000&sortOrder=-1&fetch=all`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) throw (response);

            const recordingFileDescriptorsData = (await response.json()).data;
            if (!recordingFileDescriptorsData) throw recordingFileDescriptorsData;
            this.conferenceRecordingFiles = recordingFileDescriptorsData.map(recordFileData => BubbleConferenceRecapRB.createFromData(recordFileData));

            this.logger.info("[FileStorageService] getRecordingFilesFromServer -- success");
            this.conferenceRecordingFilesSubject.next(this.conferenceRecordingFiles);

            return this.conferenceRecordingFiles;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getRecordingFilesFromServer');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async refreshConferenceRecapFromServcer(recap: BubbleConferenceRecap): Promise<void> {
        try {
            const url = config.restServerUrl + "/api/rainbow/filestorage/v1.0/conferences-recordings/" + recap.id;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) throw (response);

            const recordingFileDescriptorData = (await response.json()).data;
            if (!recordingFileDescriptorData) throw recordingFileDescriptorData;

            //update the conference recording recap
            if (this.conferenceRecordingFiles?.length) {
                //find it
                this.conferenceRecordingFiles.forEach((recording) => {
                    if (recording.id === recap.id) {
                        recording.updateFromData(recordingFileDescriptorData);
                    }
                });
            }
            // this.conferenceRecordingFiles = recordingFileDescriptorsData.map(recordFileData => BubbleConferenceRecapRB.createFromData(recordFileData));

            this.logger.info(`[FileStorageService] refreshConferenceRecapFromServcer for ${recap.id} -- success`);
            // this.conferenceRecordingFilesSubject.next(this.conferenceRecordingFiles);

            // return this.conferenceRecordingFiles;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'refreshConferenceRecapFromServcer');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public getSharedWithMeConferenceRecordingFiles(): FileDescriptor[] {
        this.sharedWithMeConferenceRecordingFilesSubject.next(this.sharedWithMeConferenceRecordingFiles);
        return this.sharedWithMeConferenceRecordingFiles;
    }

    public getConferenceRecordingFileById(fileId: string): FileDescriptor {
        let result;
        this.conferenceRecordingFileDescriptors.forEach((fileDescriptor) => {
            if (fileDescriptor && fileDescriptor.id === fileId) {
                result = fileDescriptor;
            }
        });
        if (!result) {
            this.conferenceRecordingFiles.forEach((recordingFile: BubbleConferenceRecap) => {
                if (recordingFile.associatedRecordingFiles) {
                    recordingFile.associatedRecordingFiles.forEach((fileDescriptor) => {
                        if (fileDescriptor && fileDescriptor.id === fileId) {
                            result = fileDescriptor;
                        }
                    });
                }
            });
        }
        if (!result) {
            this.sharedWithMeConferenceRecordingFiles.forEach((fileDescriptor: FileDescriptor) => {
                if (fileDescriptor && fileDescriptor.id === fileId) {
                    result = fileDescriptor;
                }
            });
        }

        return result;
    }

    public async getSharedWithMeConferenceRecordingFilesFromServer(): Promise<FileDescriptor[]> {
        try {
            const url = `${this.portalURL}/viewers/${this.contactService.userContact.dbId}?format=full&purpose=conference_record&sortField=uploadedDate&sortOrder=-1`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const recordingFileDescriptorsResponse = await response.json();
            const recordingFileDescriptorsData = recordingFileDescriptorsResponse.data;
            if (!recordingFileDescriptorsData) { throw recordingFileDescriptorsData; }

            const receivedFileDescriptors = recordingFileDescriptorsData.map((fileDescriptorData: any) => {
                return this.createFileDescriptorFromData(fileDescriptorData);
            });
            //order receivedFiles
            if (receivedFileDescriptors.length) {
                receivedFileDescriptors.forEach((fd) => {
                    if (!this.sharedWithMeConferenceRecordingFiles.find((_fileDescriptor) => { return _fileDescriptor.id === fd.id; })) {
                        //add the files that we dont already have stored
                        this.sharedWithMeConferenceRecordingFiles.unshift(fd);
                    }
                });
            }

            this.sharedWithMeConferenceRecordingFilesSubject.next(this.sharedWithMeConferenceRecordingFiles);
            this.logger.info("[FileStorageService] getSharedWithMeConferenceRecordingFiles -- success");
            return this.sharedWithMeConferenceRecordingFiles;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getSharedWithMeConferenceRecordingFiles');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async deleteConferenceRecordingFile(conferenceRecordingFileId: string): Promise<BubbleConferenceRecap[]> {
        try {
            this.logger.info("[FileStorageService] deleteConferenceRecordingFile");
            const url = `${config.restServerUrl}/api/rainbow/filestorage/v1.0/conferences-recordings/${conferenceRecordingFileId}`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) { throw (response); }
            const conferenceFileResponse = await response.json();
            const deletedConferenceFile = conferenceFileResponse.data;
            if (!deletedConferenceFile) { throw (response); }
            this.logger.info("[FileStorageService] deleteConferenceRecordingFile -- success");
            const filteredConferenceRecordingFiles: BubbleConferenceRecap[] = this.conferenceRecordingFiles.filter((recordingFile: BubbleConferenceRecap) => {
                return recordingFile.id !== deletedConferenceFile.id;
            });
            this.conferenceRecordingFiles = filteredConferenceRecordingFiles;
            this.conferenceRecordingFilesSubject.next(this.conferenceRecordingFiles);
            await this.retrieveUserConsumption();
            return this.conferenceRecordingFiles;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deleteConferenceRecordingFile');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     *
     * For a meeting a transcription may be wanted beside or not a meeting recording. For green washing need or other needs, the meeting organiser car choose whether the transcription is permanent or ephemeral. When ephemeral, the transcription is available during 15 days.
     * The aim of this API is to change the status of the recording via the boolean isEphemeral
        false : A meeting record and eventually its transcription and its summary are always available until the recording is deleted.
        true : After 15 days after the end of the conference, the meeting record is deleted. That is to say only the transcription and the summary will remain. The date timeToLiveDate is taken in account to schedule a cleaning task.
     */
    public async changeConferenceRecapEphemeral(conferenceRecordingFile: BubbleConferenceRecap, isEphemeral: boolean): Promise<void> {
        try {
            this.logger.info("[FileStorageService] changeConferenceRecapEphemeral");
            if (!conferenceRecordingFile) throw new Error('[FileStorageService] changeConferenceRecapEphemeral -- missing recap');

            const url = `${config.restServerUrl}/api/rainbow/filestorage/v1.0/conferences-recordings/${conferenceRecordingFile.id}/transcriptions`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ isEphemeral });
            const response: Response = await fetch(url, { method: 'PUT', headers, body });
            if (!response.ok) { throw (response); }
            const conferenceFileResponse = await response.json();
            const deletedConferenceFile = conferenceFileResponse.data;
            if (!deletedConferenceFile) { throw (response); }
            this.logger.info(`[FileStorageService] changeConferenceRecapEphemeral for ${conferenceRecordingFile.id} to ${isEphemeral} -- success`);
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'changeConferenceRecapEphemeral');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async getConferenceRecordingAssociatedFiles(conferenceRecordingFile: BubbleConferenceRecap): Promise<FileDescriptor[]> {
        try {
            this.logger.info("[FileStorageService] getConferenceRecordingAssociatedFiles");
            const result: FileDescriptor[] = [];
            if (!conferenceRecordingFile.associatedRecordingFiles) {
                this.logger.info("[FileStorageService] getConferenceRecordingAssociatedFiles - fetching from the server");

                conferenceRecordingFile.associatedRecordingFiles = [];
                for (let i = 0; i < conferenceRecordingFile.records.length; i++) {
                    let fileDescriptor = this.getFileDescriptorById(conferenceRecordingFile.records[i].fileId);
                    if (!fileDescriptor) {
                        fileDescriptor = await this.retrieveOneFileDescriptor(conferenceRecordingFile.records[i].fileId);
                    }
                    this.logger.info("[FileStorageService] getConferenceRecordingAssociatedFiles -- success: " + conferenceRecordingFile.records[i].fileName);
                    result.push(fileDescriptor);
                }
                conferenceRecordingFile.associatedRecordingFiles = result;
                return result;
            }
            this.logger.info("[FileStorageService] getConferenceRecordingAssociatedFiles - return results from cache");
            return conferenceRecordingFile.associatedRecordingFiles;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getConferenceRecordingAssociatedFiles');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    // **************************************************************************** //
    // ** EXTERNAL STORAGE STUFF                                                 ** //
    // **************************************************************************** //

    public async authenticateExternalStorage(): Promise<any> {
        this.logger.info(`[FileServerService] authenticateExternalStorage`);
        try {
            // Fetch the externalStorage authentication URL
            const serverUrl: URL = new URL(`${config.restServerUrl}/api/rainbow/fileserver/v1.0/external-storage/oidc-client/login?callback=${window.location.origin}/externalStorage.html`);
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(serverUrl.href, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const url = (await response.json()).data.url;

            // Add localStorage change handler
            let externalStoragePromiseResolver: any;
            let authPopup: Window;
            const localStorageHandler = async (event: StorageEvent) => {
                if (event.key === 'externalStorage' && event.newValue) {
                    this.logger.info(`[FileServerService] externalStorage -- authentication done detected -- close popup`);
                    window.removeEventListener('storage', localStorageHandler);
                    localStorage.removeItem('externalStorage');
                    if (authPopup) { authPopup.close(); authPopup = null; }
                    externalStoragePromiseResolver();
                    this.externalStorage.authenticationNeeded = false;
                }
            };
            window.addEventListener('storage', localStorageHandler, false);

            // Open the popup
            authPopup = window.open(url, "popup", "scrollbars=1,resizable=1,height=560,width=770");
            if (!authPopup || authPopup.closed || typeof authPopup.closed === 'undefined') {
                this.sendEvent(FileStorageServiceEvents.RAINBOW_FILESTORAGE_NEEDS_POPUP);
            }
            else {
                // Wait close signal
                await new Promise<any>((resolve: any) => { externalStoragePromiseResolver = resolve; });
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'authenticateExternalStorage');
            this.logger.error(`[fileServerService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async logoutExternalStorage(): Promise<void> {
        this.logger.info(`[FileServerService] logoutExternalStorage`);
        try {
            // Fetch the externalStorage authentication URL
            const serverUrl: URL = new URL(`${config.restServerUrl}/api/rainbow/fileserver/v1.0/external-storage/oidc-client/logout`);
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(serverUrl.href, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            this.externalStorage.authenticationNeeded = true;
            this.logger.info(`[fileServerService] logoutExternalStorage -- success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'logoutExternalStorage');
            this.logger.error(`[fileServerService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * @function searchForFilesOnExternalStorage
     * @description Method used to search for files on the external storage server based on different criteria
     *              Note that this method only returns my own files
     * @param {string} path define the directory to search from
     * @returns {Promise<FileDescriptor>} Object containing the { fileDescriptors: FileDescriptor[], limit: number, offset: number }
     * @memberof FileStorageService
     */
    public async searchForFilesOnExternalStorage(path: string = '', sortedBy: string = 'name', sortDirection: string = 'bottom', filterBy: string = "all", filterValue: string = '', force: boolean = false): Promise<GetFilesResultInterface> {
        try {

            // Launch authentication flow if necessary
            if (this.externalStorage.authenticationNeeded) await this.authenticateExternalStorage();

            // Get folder data from server if necessary
            if (force || !this.externalStorageFileDescriptors || this.externalStoragePath !== path) {

                const serverUrl = `${config.restServerUrl}/api/rainbow/fileserver/v1.0/external-storage/files?path=${path}`;
                const headers = this.authService.getRequestHeader();
                const response: Response = await fetch(serverUrl, { method: 'GET', headers });
                if (!response.ok) { throw (response); }
                this.externalStoragePath = path;
                const fileDescriptorsData = (await response.json()).data.content;

                // Create fileDescriptors from receive data
                this.externalStorageFileDescriptors = fileDescriptorsData.map((fdData: any) => {
                    return FileDescriptor.createFromExternalStorageData(fdData);
                });
            }

            // Convert external storage data to genericFileDescriptor format
            const fileDescriptors = this.externalStorageFileDescriptors.reduce((acc: any, fileDesc: FileDescriptor) => {

                // Apply filtering criteria
                if (filterBy !== 'all' && (fileDesc.type === 'directory' || fileDesc?.thumbnailPlaceholder.type !== filterBy)) return acc;

                if (filterValue !== '' && !fileDesc.fileNameToFilter.includes(filterValue.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, ''))) return acc;

                // Store fileDescriptor in acc
                (fileDesc.type === 'directory' ? acc.folders : acc.files).push(fileDesc);

                return acc;
            }, { folders: [], files: [] });

            // Sort files
            const descd = sortDirection === 'bottom';
            switch (sortedBy) {
                case 'file':
                    fileDescriptors.folders.sort((itA: any, itB: any) => itA.name.localeCompare(itB.name));
                    fileDescriptors.files.sort((itA: any, itB: any) => descd ? itA.thumbnailPlaceholder.icon.localeCompare(itB.thumbnailPlaceholder.icon) : itB.thumbnailPlaceholder.icon.localeCompare(itA.thumbnailPlaceholder.icon));
                    break;
                case 'name':
                    fileDescriptors.folders.sort((itA: any, itB: any) => descd ? itA.fileNameToFilter.localeCompare(itB.fileNameToFilter) : itB.fileNameToFilter.localeCompare(itA.fileNameToFilter));
                    fileDescriptors.files.sort((itA: any, itB: any) => descd ? itA.fileNameToFilter.localeCompare(itB.fileNameToFilter) : itB.fileNameToFilter.localeCompare(itA.fileNameToFilter));
                    break;
                case 'date':
                    fileDescriptors.folders.sort((itA: any, itB: any) => descd ? itA.time - itB.time : itB.time - itA.time);
                    fileDescriptors.files.sort((itA: any, itB: any) => descd ? itA.time - itB.time : itB.time - itA.time);
                    break;
                case 'size':
                    fileDescriptors.folders.sort((itA: any, itB: any) => descd ? itA.size - itB.size : itB.size - itA.size);
                    fileDescriptors.files.sort((itA: any, itB: any) => descd ? itA.size - itB.size : itB.size - itA.size);
                    break;
                default: break;
            }
            return { allReceived: true, fileDescriptors: fileDescriptors.folders.concat(fileDescriptors.files) };
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'searchForFilesOnExternalStorage');

            if (this.externalStorage && errorInfo?.error?.errorDetailsCode === 401230) {
                this.logger.warn(`[FileStorageService] searchForFilesOnExternalStorage -- ${error?.error?.errorDetails}`);
                await this.authenticateExternalStorage();
                return await this.searchForFilesOnExternalStorage(path, sortedBy, sortDirection, filterBy, filterValue, force);
            }
            this.logger.error(`[fileServerService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async shareExternalStorageFiles(path: string, basename: string, fileId: string, viewerId: string, type: string): Promise<FileDescriptor> {
        try {
            // Fetch the externalStorage authentication URL
            const serverUrl: URL = new URL(`${config.restServerUrl}/api/rainbow/fileserver/v1.0/external-storage/files/share`);
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ path, basename, fileId, viewerId, type });
            const response: Response = await fetch(serverUrl.href, { method: 'POST', headers, body });
            if (!response.ok) { throw (response); }
            const data = (await response.json()).data;

            // Create the file Descriptor
            const fd = this.createFileDescriptorFromData(data);

            const fds = fd.ownerId === this.contactService.userContact.dbId ? this.fileDescriptors : this.receivedFileDescriptors;
            if (fds.findIndex(fd2 => (fd2.id === fd.id)) === -1) fds.push(fd);
            this.logger.info(`[fileServerService] shareExternalStorageFile -- success`);
            return fd;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'shareExternalStorageFile');
            this.logger.error(`[fileServerService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async createExternalStorageSharingSpace(bubble: BubbleRB): Promise<void> {
        try {
            // Fetch the externalStorage authentication URL
            const serverUrl: URL = new URL(`${config.restServerUrl}/api/rainbow/fileserver/v1.0/external-storage/rooms/${bubble.dbId}/synchronize`);
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(serverUrl.href, { method: 'PUT', headers });
            if (!response.ok) { throw (response); }
            const data = (await response.json());
            bubble.externalStorageAccessUrl = data.data.webUrl;
            this.logger.info(`[fileServerService] createExternalStorageSharingSpace (${bubble.dbId}) : ${data.status} -- ${bubble.externalStorageAccessUrl}`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'createExternalStorageSharingSpace');
            this.logger.error(`[fileServerService] ${errorInfo.message}`);
            bubble.externalStorageAccessUrl = undefined;
            throw errorInfo.error;
        }
    }

    public async getExternalStorageSharingSpaceUrl(bubble: BubbleRB): Promise<void> {
        try {
            // Fetch the externalStorage authentication URL
            const serverUrl: URL = new URL(`${config.restServerUrl}/api/rainbow/fileserver/v1.0/external-storage/rooms/${bubble.dbId}/synchronized`);
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(serverUrl.href, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const data = (await response.json());
            bubble.externalStorageAccessUrl = data.data.webUrl;
            this.logger.info(`[fileServerService] getExternalStorageSharingSpaceUrl (${bubble.dbId}) : ${data.status} -- ${bubble.externalStorageAccessUrl}`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getExternalStorageSharingSpaceUrl');
            this.logger.error(`[fileServerService] ${errorInfo.message}`);
        }
    }

    public async createZipFromFiles(archiveName: string, files: FileDescriptor[] | BubbleConferenceRecapFile[] = []) {
        if (!files?.length) {
            throw Error("[FileStorageService] createZipFromFiles -- List of files is empty");
        }

        try {
            const fds = files.map((fd) => { return (fd.id) ? fd.id : fd.fileId; });
            if (!archiveName) archiveName = `MyArchive`;

            // Fetch the externalStorage authentication URL
            const serverUrl: URL = new URL(`${config.restServerUrl}/api/rainbow/filestorage/v1.0/files/archive`);
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ fileName: archiveName, files: fds });
            const response: Response = await fetch(serverUrl.href, { method: 'POST', headers, body });
            if (!response.ok) { throw (response); }
            const data = (await response.json());
            //the file descriptor ID to be awaited
            const fdId = data.data.fileId;
            const status = data.status;
            this.logger.info(`[fileServerService] createZipFromFiles success -- ${status} -- ${fdId}`);
            return fdId;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'createZipFromFiles');
            this.logger.error(`[fileServerService] ${errorInfo.message}`);
        }
    }

    // **************************************************************************** //
    // **                      Files transcription                               ** //
    // **************************************************************************** //

    public async getAudioFileTranscription(messageFileId: string, messageId: MessageID = null, conversation: ConversationRB = null) {

        if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.VOICEMAIL_TRANSCRIPTION)) {
            throw Error("[FileStorageService] getAudioFileTranscription -- User does not have the profile feature VOICEMAIL_TRANSCRIPTION");
        }

        try {
            let response: Response = null;
            if (conversation) {
                const params = { conversationId: conversation.dbId, msgId: messageId, conversationTo: (conversation.bubble) ? conversation.bubble.jid : conversation.contact.jid };
                const body = JSON.stringify(params);
                const serverUrl: URL = new URL(`${config.restServerUrl}/api/rainbow/filestorage/v1.0/files/${messageFileId}/transcribe`);
                const headers = this.authService.getPostHeader();
                response = await fetch(serverUrl.href, { method: 'PUT', headers, body });
            }
            else {
                const serverUrl: URL = new URL(`${config.restServerUrl}/api/rainbow/filestorage/v1.0/files/${messageFileId}/transcribe`);
                const headers = this.authService.getPostHeader();
                response = await fetch(serverUrl.href, { method: 'PUT', headers });
            }
            if (!response.ok) { throw (response); }
            const data = (await response.json());
            const transcription = data.data.transcription;
            const status = data.data.status;
            //this.transcriptionData = {fileId: messageFileId, transcriptionMessage: transcription, action: "create", error: null};
            if (response.status === 202) {
                this.logger.info(`[fileStorageService] getAudioFileTranscription submitted`);
                this.sendEvent(FileStorageServiceEvents.ON_TRANSCRIPTION_SUBMITTED, { fileId: messageFileId });
            }
            else if ((response.status === 200) && transcription) {
                this.logger.info(`[fileStorageService] getAudioFileTranscription (${transcription})`);
                this.sendEvent(FileStorageServiceEvents.ON_TRANSCRIPTION_CREATED, { fileId: messageFileId, transcriptionMessage: transcription, transcriptionStatus: status });
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getAudioFileTranscription');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);
            this.sendEvent(FileStorageServiceEvents.ON_TRANSCRIPTION_ABORTED, { fileId: messageFileId, errorDetailsCode: errorInfo.error.errorDetailsCode });
        }
    }

    public async addViewerToConferenceRecording(recap: BubbleConferenceRecap, dbId: string, type: string) {
        try {
            // Fetch the externalStorage authentication URL
            const serverUrl: URL = new URL(`${config.restServerUrl}/api/rainbow/filestorage/v1.0/conferences-recordings/${recap.id}/viewers`);
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ viewerId: dbId, type });
            const response: Response = await fetch(serverUrl.href, { method: 'POST', headers, body });
            if (!response.ok) { throw (response); }
            this.logger.info(`[fileServerService] addViewerToConferenceRecording (${recap.id}) : ${dbId} -- success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'addViewerToConferenceRecording');
            this.logger.error(`[fileServerService] ${errorInfo.message}`);
        }
    }

    public async removeViewerToConferenceRecording(recap: BubbleConferenceRecap, dbId: string) {
        try {
            // Fetch the externalStorage authentication URL
            const serverUrl: URL = new URL(`${config.restServerUrl}/api/rainbow/filestorage/v1.0/conferences-recordings/${recap.id}/viewers/${dbId}`);
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(serverUrl.href, { method: 'DELETE', headers });
            if (!response.ok) { throw (response); }
            this.logger.info(`[fileServerService] removeViewerToConferenceRecording (${recap.id}) : ${dbId} -- success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'removeViewerToConferenceRecording');
            this.logger.error(`[fileServerService] ${errorInfo.message}`);
        }
    }

    /**
     * This method is used to translate the file
     * @param {string} fileId [required] Identifier of file descriptor to translate
     * @param {string} lang [required] translation language
     * @returns {Promise<FileDescriptor>} file descriptor retrieved
     * @memberof FileStorageService
     */
    public async translateFile(fileId: string, lang: string): Promise<FileDescriptor> {
        try {
            const url = `${this.portalURL}/${fileId}/translate`;
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ language: lang });
            const response: Response = await fetch(url, { method: 'POST', headers, body });
            if (!response.ok) { throw (response); }
            const responseData = (await response.json()).data;
            this.logger.info(`[FileStorageService] translateFile | translateFile ${fileId} -- success`);
            const fileDesc = await this.retrieveAndStoreOneFileDescriptor(responseData.id, true);
            if (fileDesc.ownerId === this.contactService.userContact.dbId) { this.retrieveUserConsumption(); }
            return fileDesc;
        }
        catch (error) {

            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'translateFile');
            this.logger.error(`[fileStorageService] ${errorInfo.message}`);

            throw errorInfo.error;
        }
    }

    /**
     * Management of XMPP management message
     * @param stanza - The received stanza.
     */
    private async onManagementMessageReceived(stanza) {
        try {
            const stanzaElem: XMLElement = xmlParser(stanza);
            const transcriptionElem = stanzaElem.find("transcription");

            if (transcriptionElem) {
                const action: string = transcriptionElem.attr("action");

                // Notification about the creation of transcription
                if (action === 'create') {
                    const fileId = transcriptionElem.find("fileid").text();
                    const transcriptionMessage = transcriptionElem.find("transcriptionmessage").text();
                    const status = transcriptionElem.find("status").text();
                    this.logger.info(`[FileStorageService] onManagementMessageReceived -- create transcription: fileId: ${fileId}`);
                    this.sendEvent(FileStorageServiceEvents.ON_TRANSCRIPTION_CREATED, { fileId: fileId, transcriptionMessage: transcriptionMessage, transcriptionStatus: status });
                }

                // Notification about the abortion of transcription
                else if (action === 'abort') {
                    const fileId = transcriptionElem.find("fileid").text();
                    //const errorDetails = transcriptionElem.find("errordetails").text();
                    const errorDetailsCode = transcriptionElem.find("errordetailscode").text();
                    this.logger.info(`[FileStorageService] onManagementMessageReceived -- abort transcription fileId: ${fileId}`);
                    this.sendEvent(FileStorageServiceEvents.ON_TRANSCRIPTION_ABORTED, { fileId: fileId, errorDetailsCode: errorDetailsCode });
                }

            }
            return true;
        }
        catch (error) {
            this.logger.error(`[FileStorageService] onTranscriptionMessageReceived error: ${error}`);
            return true;
        }
    }
}
